import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import contextService from '../services/contextService';
import businessValueService from '../services/businessValuesService';
import dreadedEventService from '../services/dreadedEventsService';
import securityFrameworkService from '../services/securityFrameworkService';
import securityControlsService from '../services/securityControlsService';
import riskTreatmentService from '../services/riskTreatmentService';
import frameworkDefinitionService from '../services/frameworkDefinitionService';
import useAnalysisStore from './useAnalysisStore';
import i18n from 'i18next';

const useAtelier1Store = create(
  subscribeWithSelector((set, get) => ({
    contextData: null,
    businessValues: [],
    orphanedSupportAssets: [],
    dreadedEvents: [],
    securityFramework: null,
    controlPlan: null,
    riskTreatments: [],
    allControls: [],
    frameworkDefinitions: [],
    isDataLoaded: false,
    isLoading: false,
    error: null,
    analysisId: null,

    loadData: async (analysisId) => {
      if (!analysisId) {
        console.warn('[useAtelier1Store] No analysisId provided');
        return;
      }

      set({ isLoading: true, error: null });
      set({ analysisId });

      try {
        const promises = [
          contextService.getContext(analysisId),
          businessValueService.getBusinessValues(analysisId),
          dreadedEventService.getDreadedEvents(analysisId),
          securityFrameworkService.getSecurityFramework(analysisId),
          securityControlsService.getSecurityControls(analysisId),
          riskTreatmentService.getRiskTreatment(analysisId),
          frameworkDefinitionService.getAllFrameworkDefinitions(analysisId, i18n.language),
          securityControlsService.getAllControlDefinitions(),
        ];

        const results = await Promise.allSettled(promises);
        const [
          contextRes, bvRes, deRes, sfRes, scPlanRes, rtRes, defRes, allControlsRes
        ] = results;

        let contextData = null;
        let businessValues = [];
        let orphanedSupportAssets = [];
        let dreadedEvents = [];
        let securityFramework = null;
        let controlPlan = null;
        let riskTreatments = [];
        let allControls = [];
        let frameworkDefinitions = [];
        const errors = {};

        if (contextRes.status === 'fulfilled' && contextRes.value?.data) {
          contextData = contextService.formatContextData(contextRes.value);
        } else {
          errors.context = contextRes.reason?.message || 'Failed to load context data';
        }

        if (bvRes.status === 'fulfilled') {
          const formatted = businessValueService.formatBusinessValuesData(bvRes.value);
          businessValues = formatted.businessValues;
          orphanedSupportAssets = formatted.orphanedSupportAssets;
        }

        if (deRes.status === 'fulfilled' && deRes.value?.data?.data) {
          dreadedEvents = deRes.value.data.data.dreadedEvents ?? [];
        }

        let loadedDefinitions = [];
        if (defRes.status === 'fulfilled' && defRes.value?.success) {
          loadedDefinitions = defRes.value.data || [];
        }
        frameworkDefinitions = loadedDefinitions;

        let loadedSelections = {};
        if (sfRes.status === 'fulfilled' && sfRes.value?.data) {
          const formattedData = securityFrameworkService.formatSecurityFrameworkData(sfRes.value);
          if (formattedData && (Object.keys(formattedData.selectedRules || {}).length > 0 || (formattedData.ruleBusinessValues && Object.keys(formattedData.ruleBusinessValues).length > 0))) {
            loadedSelections = formattedData;
          }
        }
        securityFramework = {
          ...(loadedSelections || {}),
          definitions: loadedDefinitions,
        };

        if (scPlanRes.status === 'fulfilled' && scPlanRes.value) {
          controlPlan = scPlanRes.value;
        } else {
          controlPlan = { planData: {} };
        }

        if (rtRes.status === 'fulfilled' && rtRes.value?.data?.riskTreatments?.length > 0) {
          riskTreatments = riskTreatmentService.formatRiskTreatmentData(rtRes.value);
        }

        if (allControlsRes.status === 'fulfilled' && allControlsRes.value?.success && Array.isArray(allControlsRes.value.data)) {
          allControls = allControlsRes.value.data;
        }

        if (analysisId !== get().analysisId) return;
        set({
          contextData,
          businessValues,
          orphanedSupportAssets,
          dreadedEvents,
          securityFramework,
          controlPlan,
          riskTreatments,
          allControls,
          frameworkDefinitions,
          isDataLoaded: true,
          isLoading: false,
          error: Object.keys(errors).length > 0 ? errors : null,
        });
      } catch (error) {
        console.error('[useAtelier1Store] Error loading data:', error);
        set({ isLoading: false, error: { general: error.message } });
      }
    },

    saveContextData: async (contextData, onBroadcast) => {
      const analysisId = useAnalysisStore.getState().currentAnalysis?.id;
      if (!analysisId) {
        return { success: false, message: 'No analysis selected' };
      }

      try {
        const response = await contextService.saveContext(analysisId, contextData);
        if (response.success) {
          set({ contextData: contextService.formatContextData(response.data) });
          if (onBroadcast) onBroadcast('context');
        }
        return response;
      } catch (error) {
        console.error('[useAtelier1Store] Error saving context data:', error);
        return { success: false, message: error.message };
      }
    },

    saveBusinessValues: async (bvData, orphanedData, onBroadcast) => {
      const analysisId = useAnalysisStore.getState().currentAnalysis?.id;
      if (!analysisId) {
        return { success: false, message: 'No analysis selected' };
      }

      try {
        const response = await businessValueService.saveBusinessValues(analysisId, bvData, orphanedData || []);
        if (response.success) {
          const formatted = businessValueService.formatBusinessValuesData(response.data);
          set({
            businessValues: formatted.businessValues,
            orphanedSupportAssets: formatted.orphanedSupportAssets,
          });
          if (onBroadcast) onBroadcast('business-values');
        }
        return response;
      } catch (error) {
        console.error('[useAtelier1Store] Error saving business values:', error);
        return { success: false, message: error.message };
      }
    },

    saveDreadedEvents: async (deData, onBroadcast) => {
      const analysisId = useAnalysisStore.getState().currentAnalysis?.id;
      if (!analysisId) {
        return { success: false, message: 'No analysis selected' };
      }

      try {
        const response = await dreadedEventService.saveDreadedEvents(analysisId, deData);
        if (response.success) {
          set({ dreadedEvents: response.data?.data?.dreadedEvents || [] });
          if (onBroadcast) onBroadcast('events');
        }
        return response;
      } catch (error) {
        console.error('[useAtelier1Store] Error saving dreaded events:', error);
        return { success: false, message: error.message };
      }
    },

    saveSecurityFramework: async (fwData, definitionsList, onBroadcast) => {
      const analysisId = useAnalysisStore.getState().currentAnalysis?.id;
      if (!analysisId) {
        return { success: false, message: 'No analysis selected' };
      }

      try {
        const { securityFramework } = get();
        const definitionsToUse = definitionsList !== null && definitionsList !== undefined
          ? definitionsList
          : (securityFramework?.definitions || []);

        const response = await securityFrameworkService.saveSecurityFramework(analysisId, fwData);
        if (response.success) {
          const combinedData = {
            ...(securityFrameworkService.formatSecurityFrameworkData(response) || {}),
            definitions: definitionsToUse,
          };
          set({ securityFramework: combinedData });
          if (onBroadcast) onBroadcast('security');
          return { success: true, data: response.data };
        }
        return response;
      } catch (error) {
        console.error('[useAtelier1Store] Error saving security framework:', error);
        return { success: false, message: error.message };
      }
    },

    saveControlPlan: async (planData, onBroadcast) => {
      const analysisId = useAnalysisStore.getState().currentAnalysis?.id;
      if (!analysisId) {
        return { success: false, message: 'No analysis selected' };
      }

      try {
        const response = await securityControlsService.saveSecurityControls(analysisId, planData);
        if (response.success) {
          set({ controlPlan: response.data });
          if (onBroadcast) onBroadcast('security-controls');
          return { success: true, data: response.data };
        }
        return response;
      } catch (error) {
        console.error('[useAtelier1Store] Error saving control plan:', error);
        return { success: false, message: error.message };
      }
    },

    saveRiskTreatments: async (rtData, onBroadcast) => {
      const analysisId = useAnalysisStore.getState().currentAnalysis?.id;
      if (!analysisId) {
        return { success: false, message: 'No analysis selected' };
      }

      try {
        const response = await riskTreatmentService.saveRiskTreatment(analysisId, rtData);
        if (response.success) {
          set({ riskTreatments: riskTreatmentService.formatRiskTreatmentData(response.data) });
          if (onBroadcast) onBroadcast('risk-treatment');
        }
        return response;
      } catch (error) {
        console.error('[useAtelier1Store] Error saving risk treatments:', error);
        return { success: false, message: error.message };
      }
    },

    refreshFramework: async (analysisId) => {
      if (!analysisId) {
        return { success: false, message: 'No analysis ID provided' };
      }

      try {
        const sfResponse = await securityFrameworkService.getSecurityFramework(analysisId);
        let updatedSecurityFramework = null;

        if (sfResponse?.data) {
          const formattedData = securityFrameworkService.formatSecurityFrameworkData(sfResponse);
          const defResponse = await frameworkDefinitionService.getAllFrameworkDefinitions(analysisId, i18n.language);
          const definitions = defResponse?.success ? defResponse.data : [];

          updatedSecurityFramework = {
            ...(formattedData || {}),
            definitions,
          };

          set({ securityFramework: updatedSecurityFramework, frameworkDefinitions: definitions });
        }

        const controlPlanResponse = await securityControlsService.getSecurityControls(analysisId);
        if (controlPlanResponse) {
          const cleanedControlPlan = get().cleanupControlPlan(controlPlanResponse, updatedSecurityFramework);
          set({ controlPlan: cleanedControlPlan.planData });
        }

        return { success: true, data: updatedSecurityFramework };
      } catch (error) {
        console.error('[useAtelier1Store] Error refreshing framework:', error);
        return { success: false, message: error.message };
      }
    },

    cleanupControlPlan: (controlPlanData, securityFramework) => {
      if (!controlPlanData?.planData?.plan || !securityFramework?.selectedRules) {
        return { planData: controlPlanData, wasModified: false };
      }

      const selectedRuleIds = new Set();
      Object.values(securityFramework.selectedRules).forEach((rulesInFramework) => {
        if (Array.isArray(rulesInFramework)) {
          rulesInFramework.forEach((rule) => {
            if (rule?.id) {
              selectedRuleIds.add(String(rule.id));
            }
          });
        }
      });

      const originalPlan = controlPlanData.planData.plan;
      const cleanedPlan = {};
      let wasModified = false;

      Object.entries(originalPlan).forEach(([key, entry]) => {
        const [ruleId] = key.split('_');
        if (selectedRuleIds.has(String(ruleId))) {
          cleanedPlan[key] = entry;
        } else {
          wasModified = true;
        }
      });

      return {
        planData: {
          ...controlPlanData,
          planData: {
            ...controlPlanData.planData,
            plan: cleanedPlan,
          },
        },
        wasModified,
      };
    },

    reloadFrameworkDefinitions: async (analysisId) => {
      if (!analysisId) return;

      try {
        const frameworkRes = await frameworkDefinitionService.getAllFrameworkDefinitions(analysisId, i18n.language);
        if (frameworkRes.success && frameworkRes.data) {
          set((state) => ({
            frameworkDefinitions: frameworkRes.data,
            securityFramework: {
              ...state.securityFramework,
              definitions: frameworkRes.data,
            },
          }));
        }
      } catch (error) {
        console.error('[useAtelier1Store] Error reloading framework definitions:', error);
      }
    },

    refreshSlice: async (analysisId, tab) => {
      const sliceMap = {
        'context': async () => {
          const r = await contextService.getContext(analysisId);
          if (r?.data) set({ contextData: contextService.formatContextData(r) });
        },
        'business-values': async () => {
          const r = await businessValueService.getBusinessValues(analysisId);
          const f = businessValueService.formatBusinessValuesData(r);
          set({ businessValues: f.businessValues, orphanedSupportAssets: f.orphanedSupportAssets });
        },
        'events': async () => {
          const r = await dreadedEventService.getDreadedEvents(analysisId);
          if (r?.data?.data) {
            set({ dreadedEvents: r.data.data.dreadedEvents ?? [] });
          }
        },
        'security': async () => {
          await get().refreshFramework(analysisId);
        },
        'security-controls': async () => {
          const r = await securityControlsService.getSecurityControls(analysisId);
          if (r) set({ controlPlan: r });
        },
        'risk-treatment': async () => {
          const r = await riskTreatmentService.getRiskTreatment(analysisId);
          if (r?.data?.riskTreatments?.length > 0) {
            set({ riskTreatments: riskTreatmentService.formatRiskTreatmentData(r) });
          }
        },
      };
      const fn = sliceMap[tab];
      if (fn) {
        try { await fn(); }
        catch (e) { console.error(`[A1Store] refreshSlice(${tab}) failed:`, e); }
      }
    },

    resetState: () => {
      set({
        contextData: null,
        businessValues: [],
        orphanedSupportAssets: [],
        dreadedEvents: [],
        securityFramework: null,
        controlPlan: null,
        riskTreatments: [],
        allControls: [],
        frameworkDefinitions: [],
        isDataLoaded: false,
        isLoading: false,
        error: null,
        analysisId: null,
      });
    },
  }))
);

export default useAtelier1Store;